import React, { useEffect, useRef, useState } from "react"
import Modal from "aldoo-ra/Modal"
import { MagnifyingGlassIcon, MapPinIcon, ListBulletIcon, MapIcon, CheckCircleIcon, ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline"
import useT from "../../hooks/useT"
import Spinner from "../../ui/Spinner"
import { ShippingOffices } from "../../storeApi"
import { providerOf, officeAddress } from "./providers"
import BackOrCloseIcon from "../../ui/BackOrCloseIcon"
import OfficeMap from "./OfficeMap"

/** What went wrong, in a box of its own: a title, what to do, and a retry when one helps. */
function Warning({ warning, onRetry, onDismiss, retryLabel }) {
  if (!warning) return null
  return (
    <div className="flex items-start gap-3 rounded-lg border border-error/40 bg-error/10 px-4 py-3" role="alert" data-testid="office-picker-warning">
      <ExclamationTriangleIcon className="w-6 h-6 shrink-0 text-error" />
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <span className="font-bold text-[15px] text-text">{warning.title}</span>
        {warning.message && <span className="text-[14px] text-text_secondary">{warning.message}</span>}
        {warning.retry && onRetry && (
          <button type="button" onClick={onRetry} className="self-start mt-1 font-bold text-[14px] text-primary hover:underline" data-testid="office-picker-retry">
            {retryLabel}
          </button>
        )}
      </div>
      <button type="button" onClick={onDismiss} className="shrink-0 p-1 rounded-full hover:bg-error/10" aria-label="close">
        <XMarkIcon className="w-5 h-5 text-text_secondary" />
      </button>
    </div>
  )
}

/**
 * Picks the office or locker a pickup method delivers to (a delivery option
 * of store {shipping_options} with `pickup`). The list is the provider's
 * pickup points from the API: the buyer's town first, found by name or
 * street, or the nearest to where the buyer is when they let the browser
 * say. The map is the second tab: the provider's own (providers.js), or the
 * offices on our map (OfficeMap) for one without.
 */
// `near` { lat, lng }: where the parcel's recipient is (an admin moving an order: the buyer's address), the
// list opening with the offices nearest it, as "near me" does
export default function OfficePicker({ isOpen, onClose, option, city, language, selected, onPick, near = null }) {
  const t = useT()
  const [tab, setTab] = useState("list")
  const [text, setText] = useState("")
  const [point, setPoint] = useState(null)
  const [locating, setLocating] = useState(false)
  const [offices, setOffices] = useState([])
  const [pending, setPending] = useState(false)
  // { title, message, retry: () => void } — shown in the warning box
  const [warning, setWarning] = useState(null)
  const [picking, setPicking] = useState(null)
  // a failed list is asked again when this changes (the warning's retry)
  const [attempt, setAttempt] = useState(0)
  const run = useRef(0)
  const map = providerOf(option?.providerId).map
  const locker = option?.officeType === "locker"
  // the seller's own shops (pickup from the shop): looked up for that seller; "near me" and the map only
  // when there is more than one to choose between / one with its place on the map
  const shop = option?.officeType === "shop"
  const seller = option?.sellerId || undefined
  const [shopCount, setShopCount] = useState(null)
  const [shopsOnMap, setShopsOnMap] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setTab("list")
    setText("")
    setWarning(null)
    if (Number.isFinite(near?.lat) && Number.isFinite(near?.lng)) setPoint({ lat: near.lat, lng: near.lng })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // the list: a search a moment after the typing stops, the last one wins
  useEffect(() => {
    if (!isOpen || !option) return
    const id = ++run.current
    setPending(true)
    const timer = setTimeout(async () => {
      const res = await ShippingOffices({ providerId: option.providerId, method: option.method, q: text.trim() || undefined, lat: point?.lat, lng: point?.lng, city, limit: 40, sellerId: seller }).catch(() => null)
      if (id !== run.current) return
      setPending(false)
      if (!res?.offices) return setWarning({ list: true, title: shop ? t`The shops could not be loaded` : t`The offices could not be loaded`, message: t`Check your connection and try again.`, retry: () => setAttempt((n) => n + 1) })
      setWarning((current) => (current?.list ? null : current))
      setOffices(res.offices)
      if (shop && !text.trim() && !point) {
        setShopCount(res.offices.length)
        setShopsOnMap(res.offices.some((o) => Number.isFinite(Number(o.lat)) && o.lat !== null))
      }
    }, text ? 250 : 0)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, option?.key, text, point, city, attempt])

  const pick = async (id) => {
    setPicking(id)
    setWarning(null)
    const res = await ShippingOffices({ providerId: option.providerId, method: option.method, id, sellerId: seller }).catch(() => null)
    setPicking(null)
    if (res?.office) {
      onPick(res.office)
      return onClose()
    }
    // Speedy said no to this one, or the question did not get through: not the same thing
    if (res?.error === "office_not_found" && shop) return setWarning({ title: t`This shop is not available for pickups`, message: t`Choose another shop.` })
    if (res?.error === "office_not_found") {
      return setWarning({
        title: locker ? t`This locker cannot take the parcel` : t`This office cannot take the parcel`,
        message: locker ? t`It is closed or does not deliver parcels to buyers. Choose another locker.` : t`It is closed or does not deliver parcels to buyers. Choose another office.`,
      })
    }
    setWarning({ title: t`The office could not be loaded`, message: t`Check your connection and try again.`, retry: () => pick(id) })
  }

  // the provider's map says which office was picked by posting its id
  useEffect(() => {
    if (!isOpen || tab !== "map" || !map) return
    const listen = (event) => {
      if (event.origin !== map.origin) return
      const id = map.officeIdOf(event.data)
      if (id) pick(id)
    }
    window.addEventListener("message", listen)
    return () => window.removeEventListener("message", listen)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tab, map, option?.key])

  const locate = () => {
    if (!navigator.geolocation) return setWarning({ title: t`Your location is not available`, message: t`This browser cannot tell where you are. Search by town or street instead.` })
    setLocating(true)
    setWarning(null)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false)
        setText("")
        setPoint({ lat: position.coords.latitude, lng: position.coords.longitude })
      },
      () => {
        setLocating(false)
        setWarning({ title: t`Your location is not available`, message: t`Allow the browser to share your location, or search by town or street instead.`, retry: locate })
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 5 * 60 * 1000 }
    )
  }

  // a recipient's point given (an admin choosing for the buyer): "near" is near them, not this browser
  const recipient = Number.isFinite(near?.lat) && Number.isFinite(near?.lng)
  const title = shop ? t`Choose a shop` : locker ? t`Choose a locker` : t`Choose an office`
  const showNear = !shop || (shopCount ?? 0) > 1
  const showMap = !shop || shopsOnMap
  // the list as near me left it: that button is the selected one, not List
  const nearActive = tab === "list" && !!point && !text
  const selectedTab = nearActive ? "near" : tab

  return (
    <Modal isOpen={isOpen} onClose={onClose} variant="sf_dialog_back_inline" closeIcon={BackOrCloseIcon} closeLabel={t`Back`} title={title} closeOnEscape>
      <div className="flex-1 min-h-0 flex flex-col gap-3" data-testid="office-picker">
        {/* List, Map and Near me side by side, one style; the search goes under them */}
        <div className="flex flex-wrap items-center justify-center gap-2 py-2">
          <div className="flex gap-2" role="tablist">
            {[
              ["list", t`List`, ListBulletIcon],
              ...(showMap ? [["map", t`Map`, MapIcon]] : []),
            ].map(([id, label, Icon]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={selectedTab === id}
                onClick={() => {
                  // List is the plain list again: near me's results are left
                  if (id === "list") setPoint(null)
                  setTab(id)
                }}
                className={`flex items-center gap-1.5 px-3 md:px-5 h-10 rounded-full border text-[14px] font-semibold whitespace-nowrap ${selectedTab === id ? "bg-text text-paper border-text" : "border-bordered hover:border-text"}`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
          {showNear && (
          <button
            type="button"
            onClick={() => {
              setTab("list")
              if (recipient) {
                setText("")
                setPoint({ lat: near.lat, lng: near.lng })
                return
              }
              locate()
            }}
            disabled={locating}
            className={`flex items-center gap-1.5 px-3 md:px-5 h-10 rounded-full border text-[14px] font-semibold whitespace-nowrap disabled:opacity-60 text-white ${nearActive ? "bg-primary_hov border-primary_hov" : "bg-primary border-primary hover:bg-primary_hov"}`}
            data-testid="office-near-me"
          >
            <MapPinIcon className="w-4 h-4" />
            {locating ? t`Locating…` : recipient ? t`Near the buyer` : t`Near me`}
          </button>
          )}
        </div>

        {tab === "list" && (
          <>
            <div className="w-full max-w-[720px] mx-auto flex flex-col sm:flex-row gap-2">
              <label className="relative flex-1">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text_secondary pointer-events-none" />
                <input
                  value={text}
                  onChange={(e) => {
                    setText(e.target.value)
                    if (e.target.value) setPoint(null)
                  }}
                  placeholder={shop ? t`Town, shop name or street` : t`Town, office name or street`}
                  className="w-full h-[46px] pl-10 pr-3 rounded-lg border border-bordered bg-paper text-[15px] outline-none focus:border-text"
                  data-testid="office-search"
                />
              </label>
            </div>
            <Warning warning={warning} onRetry={warning?.retry} onDismiss={() => setWarning(null)} retryLabel={t`Try again`} />

            <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
              {pending && !offices.length && <Spinner className="py-10" />}
              {!pending && !offices.length && !warning && <p className="m-0 py-6 text-center text-text_secondary">{t`Nothing found. Try another town or street.`}</p>}
              {(() => {
                const card = (office, big = false) => {
                  const on = selected?.id === office.id
                  return (
                    <button
                      type="button"
                      onClick={() => pick(office.id)}
                      disabled={!!picking}
                      className={`w-full h-full flex items-start text-left hover:border-text ${big ? "gap-4 rounded-xl border-2 px-5 py-5 md:px-6" : "gap-3 rounded-lg border px-4 py-3"} ${on ? "border-text bg-neutral" : big ? "border-primary" : "border-bordered"}`}
                      data-testid={big ? "office-nearest" : "office-option"}
                    >
                      <MapPinIcon className={`${big ? "w-7 h-7" : "w-5 h-5 mt-0.5"} shrink-0 text-primary`} />
                      <span className="flex-1 min-w-0">
                        <span className={`block font-semibold ${big ? "text-[18px] md:text-[20px]" : ""}`}>{office.name}</span>
                        {office.address && <span className={`block text-text_secondary ${big ? "text-[15px] mt-1" : "text-[14px]"}`}>{officeAddress(office)}</span>}
                        {office.hours && <span className={`block text-text_secondary ${big ? "text-[14px] mt-0.5" : "text-[13px]"}`}>{office.hours}</span>}
                        {shop && office.phone && <span className={`block text-text_secondary ${big ? "text-[14px] mt-0.5" : "text-[13px]"}`}>{office.phone}</span>}
                        {shop && office.note && <span className={`block text-text_secondary italic ${big ? "text-[14px] mt-0.5" : "text-[13px]"}`}>{office.note}</span>}
                      </span>
                      {office.distanceKm != null && <span className={`shrink-0 ${big ? "text-[16px] font-bold text-primary" : "text-[13px] text-text_secondary"}`}>{office.distanceKm} km</span>}
                      {picking === office.id ? <Spinner size={18} className="w-auto py-0" /> : on && <CheckCircleIcon className="w-5 h-5 shrink-0 text-primary" />}
                    </button>
                  )
                }
                const grid = (list) => (
                  <ul className="m-0 p-0 list-none grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                    {list.map((office) => (
                      <li key={office.id} className="flex">
                        {card(office)}
                      </li>
                    ))}
                  </ul>
                )
                // near me (no search typed): the nearest office on its own, the others after it
                if (!point || text || !offices.length) return grid(offices)
                const [nearest, ...others] = offices
                return (
                  <div className="flex flex-col gap-3">
                    <p className="m-0 text-center text-[14px] font-bold text-text">{recipient ? t`Nearest to the buyer's address` : t`Nearest to your location`}</p>
                    {card(nearest, true)}
                    {others.length > 0 && <p className="m-0 mt-2 text-[14px] font-bold text-text">{t`Other locations nearby`}</p>}
                    {others.length > 0 && grid(others)}
                  </div>
                )
              })()}
            </div>
          </>
        )}

        {tab === "map" && !map && (
          // a courier with no map of its own: its offices on ours, edge to edge on a phone
          <div className="flex-1 min-h-0 flex flex-col gap-2 -mx-5 -mb-5 md:mx-0 md:mb-0">
            <OfficeMap option={option} selected={selected} point={point} offices={offices} onPick={pick} picking={picking} />
            <div className="px-5 md:px-0 empty:hidden">
              <Warning warning={warning} onRetry={warning?.retry} onDismiss={() => setWarning(null)} retryLabel={t`Try again`} />
            </div>
          </div>
        )}

        {tab === "map" && map && (
          // edge to edge on a phone: the map is the screen
          <div className="flex-1 min-h-0 flex flex-col gap-2 -mx-5 -mb-5 md:mx-0 md:mb-0">
            <iframe
              title={title}
              src={map.url({ officeType: option.officeType, language, officeId: selected?.id, city })}
              className="flex-1 min-h-0 w-full border-0 md:border md:border-bordered md:rounded-lg"
              allow="geolocation"
              data-testid="office-map"
            />
            {picking && <p className="m-0 px-5 md:px-0 text-[14px] text-text_secondary">{t`Loading the office…`}</p>}
            <div className="px-5 md:px-0 empty:hidden">
              <Warning warning={warning} onRetry={warning?.retry} onDismiss={() => setWarning(null)} retryLabel={t`Try again`} />
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
